import { useEffect, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid
} from "recharts";

const API_URL = process.env.REACT_APP_API_BASE + "/_internal/usage-dashboard";
const INTERNAL_TOKEN = process.env.REACT_APP_INTERNAL_DASHBOARD_TOKEN;

const DAILY_LIMIT_INR = 50;
const MONTHLY_BUDGET_INR = 1500;
const fmt = (v) => Number(v || 0).toFixed(2);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!INTERNAL_TOKEN) {
      setError("❌ Missing INTERNAL dashboard token.");
      return;
    }

    fetch(API_URL, { headers: { "x-internal-token": INTERNAL_TOKEN } })
      .then((res) => {
        if (!res.ok) throw new Error("❌ Unauthorized or Server Error");
        return res.json();
      })
      .then(setData)
      .catch((err) => setError(err.message));
  }, []);

  if (error) return <div style={styles.error}>{error}</div>;
  if (!data) return <div style={styles.loading}>Initializing Premium Analytics...</div>;

  const usage = data.usage || {};
  const history = data.history || {};
  const last7Days = history.last7Days || [];
  const thisMonth = history.thisMonth || { totalCalls: 0, totalCost: 0, dailyAverage: 0 };

  const monthlyUsed = thisMonth.totalCost || 0;
  const monthlyRemaining = Math.max(0, MONTHLY_BUDGET_INR - monthlyUsed);

  const freeVsPaid = [
    { name: "Remaining", value: Math.max(0, DAILY_LIMIT_INR - usage.estimatedCostTodayINR) },
    { name: "Spent", value: usage.estimatedCostTodayINR || 0 }
  ];

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>Academic Assistant Analytics</h1>
          <p style={styles.headerSub}>Real-time performance tracking for {data.date}</p>
        </div>
        <button onClick={() => exportCSV(data)} style={styles.primaryBtn}>
          <svg style={{marginRight: 8}} width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
          Export Full Report
        </button>
      </header>

      {/* SECTION 1: TODAY'S KPI CARDS */}
      <Section title="Live Status (Today)">
        <Stat label="Total Calls" value={usage.geminiCallsToday || 0} />
        <Stat label="Input Tokens" value={(usage.inputTokens || 0).toLocaleString()} />
        <Stat label="Output Tokens" value={(usage.outputTokens || 0).toLocaleString()} />
        <Stat label="Cost Today" value={`₹${fmt(usage.estimatedCostTodayINR)}`} color="#ef4444" isCurrency />
      </Section>

      {/* SECTION 2: DAILY VISUALS */}
      <div style={styles.chartRow}>
        <div style={{ flex: 2 }}>
          <Section title="Budget Utilization" isGrid={false}>
            <div style={styles.chartCard}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={freeVsPaid} dataKey="value" innerRadius={85} outerRadius={105} paddingAngle={8}>
                    <Cell fill="#10b981" />
                    <Cell fill="#f43f5e" />
                  </Pie>
                  <Tooltip formatter={(v) => `₹${fmt(v)}`} />
                </PieChart>
              </ResponsiveContainer>
              <div style={styles.chartOverlay}>
                <div style={{ fontSize: 13, color: "#94a3b8", fontWeight: 600 }}>Total Spent</div>
                <div style={{ fontSize: 26, fontWeight: 900, color: "#0f172a" }}>₹{fmt(usage.estimatedCostTodayINR)}</div>
              </div>
            </div>
          </Section>
        </div>
        
        <div style={{ flex: 1.2 }}>
          <Section title="Efficiency Metrics" isGrid={false}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <Stat label="Avg Cost / Call" value={`₹${fmt(usage.estimatedCostTodayINR / (usage.geminiCallsToday || 1))}`} />
                <Stat label="Capacity Remaining" value={1500 - (usage.geminiCallsToday || 0)} color="#10b981" />
            </div>
          </Section>
        </div>
      </div>

      {/* SECTION 3: 7-DAY TRENDS */}
      <Section title="Growth & Performance Trends" isGrid={false}>
        <div style={styles.chartCard}>
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={last7Days}>
              <defs>
                <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 11, fontWeight: 600}} dy={10} />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 11, fontWeight: 600}} />
              <Tooltip 
                contentStyle={{borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', padding: '12px'}}
                itemStyle={{fontWeight: 700, fontSize: '12px'}}
              />
              <Area yAxisId="left" type="monotone" dataKey="calls" stroke="#3b82f6" fillOpacity={1} fill="url(#colorCalls)" strokeWidth={4} name="Total Calls" />
              <Line yAxisId="left" type="monotone" dataKey="cost" stroke="#f59e0b" strokeWidth={3} dot={{r: 5, fill: '#f59e0b', strokeWidth: 2, stroke: '#fff'}} name="Daily Cost (₹)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {/* SECTION 4: MONTHLY OVERVIEW */}
      <Section title="Monthly Fiscal Summary">
          <Stat label="Monthly Volume" value={thisMonth.totalCalls.toLocaleString()} />
          <Stat label="Accrued Cost" value={`₹${fmt(thisMonth.totalCost)}`} color="#ef4444" />
          <Stat label="Burn Rate (Daily)" value={`₹${fmt(thisMonth.dailyAverage)}`} />
          <Stat label="Budget Left" value={`₹${fmt(monthlyRemaining)}`} color="#10b981" />
      </Section>
    </div>
  );
}

function Section({ title, children, isGrid = true }) { 
    return (
        <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.sectionTitleAccent}></span>
              {title}
            </h2>
            <div style={isGrid ? styles.grid : {}}>{children}</div>
        </div>
    ); 
}

function Stat({ label, value, color, isCurrency }) { 
    return (
        <div 
            style={styles.card} 
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.08)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.05)';
            }}
        >
            <div style={styles.label}>{label}</div>
            <div style={{ ...styles.value, color: color || "#0f172a" }}>{value}</div>
            <div style={styles.statFooter}>
                <span style={{color: '#10b981', fontWeight: 700}}>Live</span> tracking active
            </div>
        </div>
    ); 
}

const styles = {
  page: { 
    background: "#f8fafc", 
    minHeight: "100vh", 
    padding: "50px 80px", 
    fontFamily: "'Inter', system-ui, sans-serif",
    color: "#1e293b" 
  },
  header: { 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "flex-end", 
    marginBottom: 50 
  },
  headerTitle: { 
    margin: 0, 
    fontSize: "32px", 
    fontWeight: 900, 
    letterSpacing: "-0.03em",
    background: "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent"
  },
  headerSub: { color: "#94a3b8", margin: "8px 0 0 0", fontWeight: 500, fontSize: "15px" },
  section: { marginBottom: 60 },
  sectionTitle: { 
    fontSize: "13px", 
    fontWeight: 800, 
    color: "#64748b", 
    marginBottom: 25, 
    textTransform: "uppercase", 
    letterSpacing: "0.15em",
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },
  sectionTitleAccent: { width: "12px", height: "3px", borderRadius: "10px", background: "#3b82f6" },
  grid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "24px" },
  chartRow: { display: "flex", gap: "24px", marginBottom: "24px" },
  card: { 
    background: "#ffffff", 
    padding: "32px", 
    borderRadius: "24px", 
    border: "1px solid #f1f5f9",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    cursor: "default"
  },
  chartCard: { 
    background: "#ffffff", 
    padding: "35px", 
    borderRadius: "28px", 
    border: "1px solid #f1f5f9",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.03)",
    position: "relative" 
  },
  label: { fontSize: "11px", color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" },
  value: { fontSize: "34px", fontWeight: 900, marginTop: 12, letterSpacing: "-0.04em" },
  statFooter: { marginTop: 15, fontSize: "11px", color: "#cbd5e1", fontWeight: 500 },
  chartOverlay: { position: "absolute", top: "54%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" },
  primaryBtn: { 
    display: "flex",
    alignItems: "center",
    padding: "12px 24px", 
    background: "#0f172a", 
    color: "#fff", 
    borderRadius: "14px", 
    border: "none",
    cursor: "pointer", 
    fontWeight: 700, 
    fontSize: "13px",
    boxShadow: "0 10px 15px -3px rgba(15, 23, 42, 0.2)",
    transition: "all 0.2s"
  },
  error: { padding: 100, color: "#f43f5e", textAlign: "center", fontSize: 20, fontWeight: 900 },
  loading: { padding: 100, color: "#94a3b8", textAlign: "center", fontSize: 16, fontWeight: 600, letterSpacing: "0.05em" }
};

function exportCSV(data) {
  const history = data.history?.last7Days || [];
  const rows = [
    ["Report Date", data.date],
    ["", ""],
    ["7-Day History"],
    ["Date", "Calls", "Cost (INR)"],
    ...history.map(h => [h.date, h.calls, h.cost]),
    ["", ""],
    ["Monthly Totals"],
    ["Total Calls", data.history?.thisMonth?.totalCalls],
    ["Total Cost", data.history?.thisMonth?.totalCost]
  ];
  const csvContent = rows.map(e => e.join(",")).join("\n");
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `academic_usage_${data.date}.csv`;
  link.click();
}